const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// 서버 설정 캐시 (한번 확인 후 재사용)
let _serverConfig = null;
async function checkServerConfig() {
  if (_serverConfig !== null) return _serverConfig;
  try {
    const res = await fetch(`${BASE_URL}/server-config`);
    if (res.ok) {
      _serverConfig = await res.json();
    } else { _serverConfig = {}; }
  } catch { _serverConfig = {}; }
  return _serverConfig;
}
// 동기 버전 (이미 로드된 후에만 사용)
function getServerConfig() { return _serverConfig || {}; }
// 초기 로드 (앱 시작 시)
checkServerConfig();

export function getApiConfig() {
  return {
    appKey: localStorage.getItem("kis_app_key") || "",
    appSecret: localStorage.getItem("kis_app_secret") || "",
    token: localStorage.getItem("kis_token") || "",
    tokenExpiry: localStorage.getItem("kis_token_expiry") || "",
  };
}

export function saveApiConfig(appKey, appSecret) {
  localStorage.setItem("kis_app_key", appKey);
  localStorage.setItem("kis_app_secret", appSecret);
  localStorage.removeItem("kis_token");
  localStorage.removeItem("kis_token_expiry");
}

export function isConfigured() {
  const { appKey, appSecret } = getApiConfig();
  return !!(appKey && appSecret) || !!getServerConfig().hasKisKey;
}

async function getAccessToken() {
  const { appKey, appSecret, token, tokenExpiry } = getApiConfig();
  const serverHasKey = !!getServerConfig().hasKisKey;

  // 캐시된 토큰이 유효하면 사용
  if (token && tokenExpiry && new Date(tokenExpiry) > new Date()) return token;

  // 서버 키가 있으면 더미 값으로 요청 (서버가 실제 키로 대체)
  const reqKey = appKey || (serverHasKey ? 'SERVER_MANAGED' : '');
  const reqSecret = appSecret || (serverHasKey ? 'SERVER_MANAGED' : '');
  if (!reqKey) throw new Error("API 키가 설정되지 않았습니다");

  const res = await fetch(`${BASE_URL}/oauth2/tokenP`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ grant_type: "client_credentials", appkey: reqKey, appsecret: reqSecret }),
    mode: "cors",
  });
  if (!res.ok) throw new Error(`토큰 발급 실패: ${res.status}`);
  const data = await res.json();
  if (!data.access_token) throw new Error(data.message || "토큰 발급 실패");
  localStorage.setItem("kis_token", data.access_token);
  localStorage.setItem("kis_token_expiry", data.access_token_token_expired);
  return data.access_token;
}

async function kisGet(path, trId, params) {
  const token = await getAccessToken();
  const { appKey, appSecret } = getApiConfig();
  const serverHasKey = !!getServerConfig().hasKisKey;
  const hdrKey = appKey || (serverHasKey ? 'SERVER_MANAGED' : '');
  const hdrSecret = appSecret || (serverHasKey ? 'SERVER_MANAGED' : '');
  const url = new URL(`${BASE_URL}${path}`);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), {
    headers: { "authorization": `Bearer ${token}`, "appkey": hdrKey, "appsecret": hdrSecret, "tr_id": trId, "custtype": "P" },
  });
  if (!res.ok) throw new Error(`API 오류: ${res.status}`);
  const data = await res.json();
  if (data.rt_cd !== "0") throw new Error(data.msg1 || "API 오류");
  return data;
}

export async function fetchStockInfo(stockCode) {
  const data = await kisGet("/uapi/domestic-stock/v1/quotations/inquire-price", "FHKST01010100", {
    fid_cond_mrkt_div_code: "J", fid_input_iscd: stockCode,
  });
  const o = data.output;
  return {
    price: parseInt(o.stck_prpr),
    change: parseInt(o.prdy_vrss),
    changePct: parseFloat(o.prdy_ctrt),
    week52High: parseInt(o.w52_hgpr),
    week52Low: parseInt(o.w52_lwpr),
    marketCap: o.hts_avls,
    per: parseFloat(o.per),
    pbr: parseFloat(o.pbr),
    eps: parseInt(o.eps),
    bps: parseInt(o.bps),
    name: o.hts_kor_isnm,
    listedShares: parseInt(o.lstn_stcn) || null,
  };
}

export async function fetchInvestorTrend(stockCode) {
  const data = await kisGet("/uapi/domestic-stock/v1/quotations/inquire-investor", "FHKST01010900", {
    fid_cond_mrkt_div_code: "J", fid_input_iscd: stockCode,
  });
  return (data.output || []).slice(0, 5).map(d => ({
    date: `${d.stck_bsop_date.slice(4,6)}/${d.stck_bsop_date.slice(6,8)}`,
    individual: parseInt(d.prsn_ntby_qty) || 0,
    foreign: parseInt(d.frgn_ntby_qty) || 0,
    institution: parseInt(d.orgn_ntby_qty) || 0,
    programNet: parseInt(d.pgm_ntby_qty) || 0,
  }));
}

// 프로그램매매 종목별 일별 추이
export async function fetchProgramTrade(stockCode) {
  try {
    const data = await kisGet("/uapi/domestic-stock/v1/quotations/program-trade-by-stock-daily", "FHPPG04650100", {
      fid_input_iscd: stockCode,
      fid_cond_mrkt_div_code: "J",
      fid_input_date_1: "",
      fid_input_date_2: "",
    });
    return (data.output1 || data.output || []).slice(0, 5).map(d => ({
      date: d.stck_bsop_date ? `${d.stck_bsop_date.slice(4,6)}/${d.stck_bsop_date.slice(6,8)}` : "",
      programBuy: parseInt(d.pgm_buy_qty || d.toda_pgm_buy_qty) || 0,
      programSell: parseInt(d.pgm_sell_qty || d.toda_pgm_sell_qty || d.pgm_sll_qty) || 0,
      programNet: parseInt(d.pgm_ntby_qty || d.toda_pgm_ntby_qty) || 0,
    }));
  } catch (e) {
    console.error("[프로그램매매 오류]", e.message);
    return [];
  }
}

// KRX 세션 관리
export function getKrxSession() {
  return localStorage.getItem("krx_session") || "";
}
export function saveKrxSession(session) {
  localStorage.setItem("krx_session", session);
}
export function isKrxConfigured() {
  return !!getKrxSession();
}



// 공매도 일별 추이 (서버가 KRX 자동/수동 세션으로 처리)
export async function fetchShortSelling(stockCode) {
  try {
    const session = getKrxSession();
    const query = session ? `?session=${encodeURIComponent(session)}` : '';
    const res = await fetch(`${BASE_URL}/krx/shortselling/${stockCode}${query}`);
    if (!res.ok) return [];
    const json = await res.json();
    const rows = Array.isArray(json) ? json : (json.data || []);
    return rows.map(d => {
      const raw = (d.date || "").replace(/\//g, "");
      const date = raw.length >= 8 ? `${raw.slice(4,6)}/${raw.slice(6,8)}` : d.date;
      return {
        date,
        shortVolume: d.shortVolume || 0,
        shortBalance: d.totalVolume || 0,
        shortRatio: d.shortRatio || 0,
      };
    });
  } catch (e) {
    console.error("[공매도 오류]", e.message);
    return [];
  }
}

// 공매도 잔고 (서버가 KRX 자동 세션으로 처리)
export async function fetchShortBalance(stockCode) {
  try {
    const res = await fetch(`${BASE_URL}/shortbalance/${stockCode}`);
    if (!res.ok) return [];
    const json = await res.json();
    return (json.data || []).map(d => {
      const raw = (d.date || "").replace(/\//g, "");
      const date = raw.length >= 8 ? `${raw.slice(4,6)}/${raw.slice(6,8)}` : d.date;
      return {
        date,
        shortBalance: d.shortBalance || 0,
        listedShares: d.listedShares || 0,
        shortBalanceRatio: d.shortBalanceRatio || 0,
      };
    });
  } catch (e) {
    console.error("[공매도 잔고 오류]", e.message);
    return [];
  }
}


export async function fetchFinancials(stockCode) {
  const data = await kisGet("/uapi/domestic-stock/v1/finance/financial-ratio", "FHKST66430300", {
    fid_div_cls_code: "1", fid_input_iscd: stockCode,
  });
  const result = {};
  (data.output || []).forEach(d => {
    const year = d.stac_yymm?.slice(0, 4);
    const month = d.stac_yymm?.slice(4, 6);
    if (!year || !month) return;
    const qKey = month === "03" ? "1Q" : month === "06" ? "2Q" : month === "09" ? "3Q" : "4Q";
    if (!result[year]) result[year] = {};
    result[year][qKey] = {
      revenue: Math.round(parseFloat(d.sale_totl_prfi) || 0),
      operating: Math.round(parseFloat(d.bsop_prfi) || 0),
      net: Math.round(parseFloat(d.thtr_ntin) || 0),
    };
  });
  Object.keys(result).forEach(year => {
    const qs = result[year];
    const qList = ["1Q","2Q","3Q","4Q"].filter(q => qs[q]);
    if (qList.length > 0) {
      result[year]["연간"] = {
        revenue: qList.reduce((a, q) => a + (qs[q]?.revenue || 0), 0),
        operating: qList.reduce((a, q) => a + (qs[q]?.operating || 0), 0),
        net: qList.reduce((a, q) => a + (qs[q]?.net || 0), 0),
      };
    }
  });
  return result;
}

// 차트 데이터 페이징 공통 함수 (KIS API는 한 번에 ~100개만 반환)
async function fetchChartPaged(stockCode, periodCode, yearsBack, maxPages) {
  const fmtDate = (d) => d.toISOString().slice(0, 10).replace(/-/g, "");
  const parseRow = (d) => ({
    date: d.stck_bsop_date,
    close: parseInt(d.stck_clpr) || 0,
    high: parseInt(d.stck_hgpr) || 0,
    low: parseInt(d.stck_lwpr) || 0,
    open: parseInt(d.stck_oprc) || 0,
    volume: parseInt(d.acml_vol) || 0,
  });

  const globalEnd = new Date();
  const globalStart = new Date();
  globalStart.setFullYear(globalEnd.getFullYear() - yearsBack);

  let allData = [];
  let curEnd = new Date(globalEnd);

  for (let page = 0; page < maxPages; page++) {
    if (curEnd <= globalStart) break;
    try {
      const data = await kisGet("/uapi/domestic-stock/v1/quotations/inquire-daily-itemchartprice", "FHKST03010100", {
        fid_cond_mrkt_div_code: "J",
        fid_input_iscd: stockCode,
        fid_input_date_1: fmtDate(globalStart),
        fid_input_date_2: fmtDate(curEnd),
        fid_period_div_code: periodCode,
        fid_org_adj_prc: "0",
      });
      const items = (data.output2 || []).map(parseRow).filter(d => d.close > 0);
      if (items.length === 0) break;
      allData = allData.concat(items);

      // 가장 오래된 데이터의 날짜 - 1일을 다음 페이지 끝점으로
      const oldest = items[items.length - 1].date; // output2는 최신→과거 순
      const y = parseInt(oldest.slice(0, 4));
      const m = parseInt(oldest.slice(4, 6)) - 1;
      const d = parseInt(oldest.slice(6, 8));
      curEnd = new Date(y, m, d - 1);

      if (items.length < 30) break; // 데이터가 적으면 더 이상 없음
    } catch {
      break;
    }
  }

  // 중복 제거 + 날짜순 정렬
  const seen = new Set();
  return allData.filter(d => {
    if (seen.has(d.date)) return false;
    seen.add(d.date);
    return true;
  }).sort((a, b) => a.date.localeCompare(b.date));
}

export async function fetchDailyPrice(stockCode) {
  return fetchChartPaged(stockCode, "D", 2, 5); // 2년, 최대 5페이지 (~500일)
}

export async function fetchWeeklyPrice(stockCode) {
  return fetchChartPaged(stockCode, "W", 5, 3); // 5년, 최대 3페이지 (~260주)
}

export async function fetchMonthlyPrice(stockCode) {
  return fetchChartPaged(stockCode, "M", 10, 2); // 10년, 최대 2페이지 (~120월)
}

// AI API 설정 (Gemini / Claude)
export function getAiConfig() {
  return {
    provider: localStorage.getItem("ai_provider") || "gemini",
    apiKey: localStorage.getItem("ai_api_key") || "",
    model: localStorage.getItem("ai_model") || "",
  };
}

export function saveAiConfig(provider, apiKey, model) {
  localStorage.setItem("ai_provider", provider);
  localStorage.setItem("ai_api_key", apiKey);
  localStorage.setItem("ai_model", model || "");
}

export function isAiConfigured() {
  return !!getAiConfig().apiKey || !!getServerConfig().hasGeminiKey;
}

// Perplexity API 설정 (웹 검색 기반 분석)
export function getPerplexityConfig() {
  return {
    apiKey: localStorage.getItem("perplexity_api_key") || "",
    model: localStorage.getItem("perplexity_model") || "",
  };
}

export function savePerplexityConfig(apiKey, model) {
  localStorage.setItem("perplexity_api_key", apiKey);
  localStorage.setItem("perplexity_model", model || "");
}

export function isPerplexityConfigured() {
  return !!getPerplexityConfig().apiKey || !!getServerConfig().hasPerplexityKey;
}

export async function fetchAiAnalysis(stockData) {
  const ai = getAiConfig();
  const pplx = getPerplexityConfig();
  const serverCfg = getServerConfig();
  // 서버에 키가 있으면 클라이언트 키 없어도 진행
  if (!ai.apiKey && !pplx.apiKey && !serverCfg.hasGeminiKey && !serverCfg.hasPerplexityKey) {
    throw new Error("AI API 키가 설정되지 않았습니다");
  }

  // 서버에 키가 있으면 클라이언트 키를 전송하지 않음
  const payload = { provider: ai.provider || 'gemini', model: ai.model || '', stockData };
  if (!serverCfg.hasGeminiKey) payload.apiKey = ai.apiKey || '';
  if (!serverCfg.hasPerplexityKey) {
    payload.perplexityKey = pplx.apiKey || '';
    payload.perplexityModel = pplx.model || '';
  }
  const res = await fetch(`${BASE_URL}/ai-analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "AI 분석 요청 실패");
  }
  return res.json();
}

export function getDartApiKey() {
  return localStorage.getItem("dart_api_key") || "";
}

export function saveDartApiKey(key) {
  localStorage.setItem("dart_api_key", key);
}

export function isDartConfigured() {
  return !!getDartApiKey() || !!getServerConfig().hasDartKey;
}

// Fetch DART disclosures (공시사항)
export async function fetchDartDisclosures(stockCode, count = 5) {
  const dartKey = getDartApiKey();
  const hasDart = dartKey || getServerConfig().hasDartKey;
  if (!hasDart) return [];
  const res = await fetch(`${BASE_URL}/dart/disclosure/${stockCode}?count=${count}&dartKey=${dartKey || 'SERVER'}`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.list || [];
}

// Fetch DART financial statements (for historical data)
export async function fetchDartFinancials(stockCode, year) {
  const dartKey = getDartApiKey();
  const hasDart = dartKey || getServerConfig().hasDartKey;
  if (!hasDart) return null;
  const res = await fetch(`${BASE_URL}/dart/financial/${stockCode}/${year}?dartKey=${dartKey || 'SERVER'}`);
  if (!res.ok) return null;
  return await res.json();
}

// Fetch DART 전체 재무제표 (2020~현재, 한 번에)
export async function fetchDartFinancialsAll(stockCode) {
  const dartKey = getDartApiKey();
  const hasDart = dartKey || getServerConfig().hasDartKey;
  if (!hasDart) return null;
  const res = await fetch(`${BASE_URL}/dart/financialAll/${stockCode}?dartKey=${dartKey || 'SERVER'}`);
  if (!res.ok) return null;
  return await res.json();
}

// FnGuide 재무제표 (DART 폴백용)
export async function fetchFnGuideFinancials(stockCode) {
  try {
    const res = await fetch(`${BASE_URL}/fnguide/${stockCode}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data && Object.keys(data).length > 0 ? data : null;
  } catch { return null; }
}

// 발행주식수/유동주식 정보
export async function fetchShareInfo(stockCode) {
  try {
    const res = await fetch(`${BASE_URL}/shareInfo/${stockCode}`);
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

// 종목 자동완성 검색 (서버 퍼지 매칭)
export async function searchStocks(query) {
  if (!query || query.trim().length === 0) return [];
  try {
    const res = await fetch(`${BASE_URL}/stockSearch?q=${encodeURIComponent(query.trim())}`);
    if (!res.ok) return [];
    return await res.json();
  } catch { return []; }
}

// 선물 근월물 만기 YYMM 계산
function getNearestFuturesYYMM() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const quarters = [3, 6, 9, 12];
  for (const q of quarters) {
    if (q > m || (q === m && now.getDate() <= 14)) {
      return `${String(y % 100).padStart(2, '0')}${String(q).padStart(2, '0')}`;
    }
  }
  return `${String((y + 1) % 100).padStart(2, '0')}03`;
}

// 코스피200/코스닥150 선물 시세 조회 (KIS API)
export async function fetchFuturesIndices() {
  if (!isConfigured()) return [];
  const yymm = getNearestFuturesYYMM();
  const targets = [
    { code: `101S${yymm}`, symbol: 'KOSPI_NIGHT' },
    { code: `106S${yymm}`, symbol: 'KOSDAQ_NIGHT' },
  ];
  const results = [];
  for (const t of targets) {
    try {
      const data = await kisGet(
        '/uapi/domestic-futureoption/v1/quotations/inquire-price',
        'FHMIF10000000',
        { fid_cond_mrkt_div_code: 'F', fid_input_iscd: t.code }
      );
      const o = data.output;
      if (o) {
        const price = parseFloat(o.futs_prpr || o.stck_prpr || o.prpr) || 0;
        const change = parseFloat(o.futs_prdy_vrss || o.prdy_vrss) || 0;
        const changePct = parseFloat(o.futs_prdy_ctrt || o.prdy_ctrt) || 0;
        if (price > 0) results.push({ symbol: t.symbol, price, change, changePct });
      }
    } catch (e) {
      console.error(`[선물시세 ${t.symbol}]`, e.message);
    }
  }
  return results;
}

// 시가총액 TOP5 (서버 → 네이버 API 기반)
export async function fetchMarketCapTop() {
  try {
    const res = await fetch(`${BASE_URL}/cap-top5`);
    if (!res.ok) throw new Error(`cap-top5 오류: ${res.status}`);
    return await res.json();
  } catch (e) {
    console.error('[시총TOP]', e.message);
    return { kospi: [], kosdaq: [] };
  }
}

export async function searchStockCode(name) {
  const KNOWN = {
    "삼성전자": "005930", "sk하이닉스": "000660", "현대차": "005380",
    "기아": "000270", "naver": "035420", "카카오": "035720",
    "에코프로": "086520", "에코프로비엠": "247540", "에이텀": "355690",
    "젬백스": "082270", "한미반도체": "042700", "삼성sdi": "006400",
    "lg화학": "051910", "현대모비스": "012330", "포스코홀딩스": "005490",
    "셀트리온": "068270", "kb금융": "105560", "신한지주": "055550",
    "hpsp": "403870", "티엘비": "417200", "로보티즈": "108490",
    "gst": "083450", "gsdt": "083450", "티로보틱스": "117730",
    "뉴로메카": "348350", "isc": "095340", "리노공업": "058470",
  };
  const key = name.toLowerCase().replace(/\s/g, "");
  if (KNOWN[key]) return KNOWN[key];
  if (/^\d{6}$/.test(name)) return name;
  return null;
}

export async function fetchMarketMovers() {
  const dartKey = getDartApiKey();
  const url = dartKey ? `${BASE_URL}/market-movers?dartKey=${encodeURIComponent(dartKey)}` : `${BASE_URL}/market-movers`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("시장 분석 데이터 로딩 실패");
  return res.json();
}

export async function fetchYoutubeOcean() {
  try {
    const res = await fetch(`${BASE_URL}/youtube-ocean`);
    if (!res.ok) return [];
    return res.json();
  } catch { return []; }
}
